import { Container } from '@/atoms/Container/Container';
import { Skeleton } from '@/atoms/Skeleton/Skeleton';
import { cn } from '@/libs/utils/utils';

interface ResourceCardSkeletonProps {
  /** Also hold the place of the bottom zone (action row and tags; a card in a post); not while composing. */
  withActions?: boolean;
  /** `large`: the resource page's large card, with its wider padding, bigger image and title from `sm`. */
  size?: 'default' | 'large';
}

/**
 * Loading state of `ResourceCard`, in its frame: type and source on top, image and title beside
 * it, then the bottom zone (tag label and actions, then the tags) under one divider.
 */
export function ResourceCardSkeleton({ withActions = false, size = 'default' }: ResourceCardSkeletonProps) {
  const isLarge = size === 'large';
  return (
    <Container
      overrideDefaults
      data-testid="resource-card-skeleton"
      data-size={size}
      className="flex w-full min-w-0 flex-col divide-y divide-card overflow-hidden rounded-md bg-muted"
    >
      <Container overrideDefaults className={cn('flex flex-col gap-3 p-4', isLarge && 'sm:p-6')}>
        <Container overrideDefaults className="flex items-center justify-between gap-3">
          <Skeleton className="h-4 w-20 rounded-md" />
          <Skeleton className="h-4 w-24 rounded-md" />
        </Container>
        <Container overrideDefaults className="flex gap-4">
          <Skeleton className={cn('shrink-0 rounded-md', isLarge ? 'size-24 sm:size-40' : 'size-20 sm:size-24')} />
          <Container className="min-w-0 flex-1 gap-2">
            <Skeleton className={cn('w-48 max-w-full rounded-md', isLarge ? 'h-7 sm:h-8 sm:w-72' : 'h-5')} />
            <Skeleton className="h-4 w-full rounded-md" />
            <Skeleton className="h-4 w-32 rounded-md" />
          </Container>
        </Container>
      </Container>
      {withActions && (
        <Container
          overrideDefaults
          data-testid="resource-card-skeleton-actions"
          className={cn('flex flex-col gap-2 px-4 py-3', isLarge && 'sm:gap-3 sm:px-6 sm:py-4')}
        >
          <Container overrideDefaults className="flex items-center justify-between gap-3">
            <Skeleton className="h-3 w-32 rounded-md" />
            <Container overrideDefaults className="flex gap-2">
              <Skeleton className="h-8 w-10 rounded-md" />
              <Skeleton className="h-8 w-10 rounded-md" />
              <Skeleton className="h-8 w-10 rounded-md" />
            </Container>
          </Container>
          <Skeleton className="size-8 rounded-md" />
        </Container>
      )}
    </Container>
  );
}
